import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, ChevronRight } from 'lucide-react';
import api, { setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export default function OrgPicker({ choice, onTwoFactor, onBack }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);

  const select = async (userId) => {
    if (pending) return;
    setPending(userId);
    setError(null);
    try {
      const { data } = await api.post('/auth/select-org', { challenge: choice.challenge, user_id: userId });
      if (data.requires_2fa && data.challenge) {
        if (onTwoFactor) onTwoFactor(data);
        else setError('Ce compte exige une vérification en deux étapes — connectez-vous par email + mot de passe.');
        return;
      }
      if (data.session_token) setToken(data.session_token);
      setUser(data.user);
      navigate(data.redirect_path || '/app', { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Sélection impossible — réessayez.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div data-testid="org-picker" className="space-y-3">
      <div>
        <h2 className="text-[15px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Choisissez votre organisme
        </h2>
        <p className="text-[12.5px] text-[#6B6C85] mt-0.5">
          Votre email est rattaché à plusieurs centres de formation. Sélectionnez celui auquel vous souhaitez accéder.
        </p>
      </div>
      <div className="space-y-2">
        {(choice.organizations || []).map((o) => (
          <button
            key={o.user_id}
            type="button"
            data-testid={`org-choice-${o.org_id}`}
            onClick={() => select(o.user_id)}
            disabled={!!pending}
            className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-[#EFF0F6] bg-white hover:border-[#4F46E5] hover:bg-[#F8F7FF] transition-colors text-left disabled:opacity-60"
          >
            <div className="w-10 h-10 rounded-xl bg-[#EDE8FF] flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-[#4F46E5]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-[#1A1B2E] truncate">{o.org_name}</div>
              <div className="text-[12px] text-[#8E8FA5]">{o.role || 'Utilisateur'}</div>
            </div>
            {pending === o.user_id
              ? <Loader2 className="w-4 h-4 animate-spin text-[#4F46E5]" />
              : <ChevronRight className="w-4 h-4 text-[#B8B9CC]" />}
          </button>
        ))}
      </div>
      {error && (
        <div data-testid="org-picker-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
      )}
      {onBack && (
        <button
          type="button"
          data-testid="org-picker-back"
          onClick={onBack}
          className="w-full text-[12.5px] text-[#6B6C85] hover:text-[#4F46E5] font-semibold"
        >
          Utiliser un autre compte
        </button>
      )}
    </div>
  );
}
